// axiosInstance.ts
import { AxiosError, type AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { enqueueSnackbar } from 'notistack';

import { HttpClient } from './httpClient';

const token = Cookies.get('ACCESS_TOKEN');
const tenantId = Cookies.get('USER_TENANT_ID');

/** Extended config: set autoHandleError to false to skip automatic error snackbar. Default true. */
export interface AwAxiosRequestConfig extends InternalAxiosRequestConfig {
	autoHandleError?: boolean;
}

/**
 * Response success interceptor
 */
const handleResponseSuccess = (response: AxiosResponse) => {
	const autoHandleError = (response.config as AwAxiosRequestConfig)?.autoHandleError !== false;
	if (autoHandleError && !!response?.data?.code && response?.data?.code !== 200) {
		enqueueSnackbar(response.data.msg || 'An error occurred', { variant: 'error' });
	}
	return response;
};

/**
 * Response error interceptor
 */
const handleResponseError = (error: AxiosError) => {
	const config = error.config as AwAxiosRequestConfig | undefined;
	const autoHandleError = config?.autoHandleError !== false;

	if (error.response) {
		const { status, data } = error.response;
		if (status === 401) {
			if (process.env.NODE_ENV === 'development') {
				setTimeout(() => {
					globalThis.location.replace(`${process.env.REACT_APP_PORTAL_DOMAIN}?redirect=${encodeURIComponent(globalThis.location.href)}`);
				}, 2000);
			}
			enqueueSnackbar('Session expired,please refresh the page.', { variant: 'warning' });
		} else if (status === 403) {
			enqueueSnackbar('You do not have permission to perform this action.', { variant: 'warning' });
		} else if (autoHandleError) {
			console.log('Response error status:', status, data);
			enqueueSnackbar((data as any)?.msg || (data as any)?.message || 'Server busy, please try again later.', { variant: 'error' });
		}
	}
	if (autoHandleError) return {};
	return Promise.reject(error instanceof Error ? error : new Error(String(error)));
}

/**
 * Factory function: Create specific service Axios instance
 * @param serviceName Corresponds to the key in ARTWORK_ENV.environment.moduleUrls, e.g., 'artwork', 'cm'
 */
export const createServiceAxios = (serviceName: string) => {
	const client = new HttpClient({
		baseURL: "/", // Initial placeholder, will be overridden by interceptor
		headers: {
			'Authorization': `Bearer ${token}`,
			'tenant-id': tenantId,
		},
		timeout: 30000,
	});

	// Add request interceptor: Dynamically set Base URL
	client.instance.interceptors.request.use(
		(config) => {
			const env = (globalThis as any).ARTWORK_ENV;
			// Dynamically get the corresponding service URL
			const serviceUrl = env?.environment?.moduleUrls?.[serviceName];

			if (serviceUrl) {
				config.baseURL = serviceUrl;
			}
			return config;
		},
		(error) => Promise.reject(error)
	);

	// Add response interceptor
	client.instance.interceptors.response.use(handleResponseSuccess, handleResponseError);

	return client;
};

// Default export (for backward compatibility, defaults to artwork service)
export default createServiceAxios('artwork');
